import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  LuChevronLeft, LuChevronRight,
  LuTruck, LuPackage, LuHeadphones, LuCircleCheck,
  LuShieldCheck, LuZap, LuArrowRight,
} from "react-icons/lu";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import FeaturedProducts from "../components/FeaturedProducts";
import useCachedProducts from "../hooks/UseCachedProducts";
import { getPrimaryImage } from "../utils/imageUtils";
import { useAuth } from "../context/AuthContext";
import ScrollReveal from "../components/ScrollReveal";

const CATEGORIES = [
  { label: "Vapes",   path: "/products/disposable-vapes", slugs: ["disposable-vapes", "vapes"] },
  { label: "Kratom",  path: "/products/kratom",           slugs: ["kratom"] },
  { label: "Novelty", path: "/products/novelty",          slugs: ["novelty"] },
  { label: "Glass",   path: "/products/glass",            slugs: ["glass"] },
  { label: "Delta",   path: "/products/delta",            slugs: ["delta"] },
];

const TRUST_STATS = [
  { stat: "500+",    label: "Retail Partners" },
  { stat: "24–48hr", label: "Local Delivery" },
  { stat: "100+",    label: "Brands in Stock" },
  { stat: "$500+",   label: "Free Shipping" },
];

function Home() {
  const allProducts  = useCachedProducts();
  const { user }     = useAuth();
  const [heroIdx,    setHeroIdx]    = useState(0);
  const [paused,     setPaused]     = useState(false);

  const heroProducts = useMemo(
    () => allProducts.filter(p => p.heroActive),
    [allProducts]
  );

  const goTo   = useCallback((idx) => setHeroIdx(idx), []);
  const goPrev = useCallback(() => setHeroIdx(i => (i - 1 + heroProducts.length) % heroProducts.length), [heroProducts.length]);
  const goNext = useCallback(() => setHeroIdx(i => (i + 1) % heroProducts.length), [heroProducts.length]);

  useEffect(() => {
    if (heroProducts.length <= 1 || paused) return;
    const t = setInterval(goNext, 6000);
    return () => clearInterval(t);
  }, [heroProducts.length, paused, goNext]);

  useEffect(() => { setHeroIdx(0); }, [heroProducts.length]);

  const [newProducts, bestSellers] = useMemo(() => {
    const shuffled = [...allProducts].sort(() => 0.5 - Math.random());
    return [shuffled.slice(0, 8), shuffled.slice(8, 16)];
  }, [allProducts]);

  const categoryImages = useMemo(() => {
    const map = {};
    CATEGORIES.forEach(({ label, slugs }) => {
      const matches = allProducts.filter(p => slugs.some(s => p.category?.toLowerCase() === s));
      const pick = matches.length ? matches[Math.floor(Math.random() * matches.length)] : null;
      map[label] = pick ? getPrimaryImage(pick) : "/images/pictureComingSoon.jpg";
    });
    return map;
  }, [allProducts]);

  return (
    <div className="bg-background text-foreground">
      <Helmet>
        <title>Khoja Distributions | Wholesale Vapes, Kratom & More — DFW, Texas</title>
        <meta name="description" content="DFW-based B2B wholesale distributor of disposable vapes, kratom, delta products, glass, and novelty items. Local delivery in 24–48 hrs. Apply for wholesale access today." />
        <link rel="canonical" href="https://www.khojadistributions.com/" />
        <meta property="og:title" content="Khoja Distributions | Wholesale Vapes, Kratom & More — DFW, Texas" />
        <meta property="og:description" content="DFW-based B2B wholesale distributor of disposable vapes, kratom, delta products, glass, and novelty items. Local delivery in 24–48 hrs." />
        <meta property="og:url" content="https://www.khojadistributions.com/" />
      </Helmet>

      {/* ── Hero ───────────────────────────────────────────────────────────────── */}
      <section
        className="relative min-h-[88vh] flex items-center overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Background slides */}
        {heroProducts.length > 0 ? (
          heroProducts.map((p, i) => (
            <div
              key={p.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${i === heroIdx ? "opacity-100" : "opacity-0"}`}
            >
              <img
                src={p.heroImageUrl || getPrimaryImage(p)}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/88 to-brand-navy/25" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-transparent to-transparent" />
            </div>
          ))
        ) : (
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 grid grid-cols-4 gap-0 opacity-[0.12]">
              {allProducts.slice(0, 8).map((p, i) => (
                <div key={i} className="overflow-hidden">
                  <img src={getPrimaryImage(p)} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/95 to-brand-navy/60" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-transparent to-transparent" />
          </div>
        )}

        {/* Hero content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-32">
          {heroProducts.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

              {/* Left: product info */}
              <div key={heroProducts[heroIdx]?.id} className="animate-fade-in-up space-y-6 max-w-xl">
                <div className="flex items-center gap-3">
                  {heroProducts[heroIdx]?.category && (
                    <span className="px-3 py-1 rounded-full bg-brand-amber/15 border border-brand-amber/30 text-brand-amber text-[11px] font-bold uppercase tracking-widest">
                      {heroProducts[heroIdx].category}
                    </span>
                  )}
                  <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/50 text-[11px] font-semibold uppercase tracking-widest">
                    Wholesale Only
                  </span>
                </div>

                <h1
                  className="text-4xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.08]"
                  style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55), 0 1px 4px rgba(0,0,0,0.35)" }}
                >
                  {heroProducts[heroIdx]?.name}
                </h1>

                {heroProducts[heroIdx]?.description && (
                  <p className="text-base text-slate-400 leading-relaxed max-w-md">
                    {heroProducts[heroIdx].description.length > 140
                      ? heroProducts[heroIdx].description.slice(0, 140) + "…"
                      : heroProducts[heroIdx].description}
                  </p>
                )}

                {user && heroProducts[heroIdx]?.price != null && (
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-black text-white">
                      ${heroProducts[heroIdx].price.toFixed(2)}
                    </span>
                    <span className="text-slate-400 text-sm font-medium">/ unit · wholesale</span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <Link
                    to={`/products/details/${heroProducts[heroIdx]?.id}`}
                    className="bg-brand-amber hover:brightness-110 text-white px-7 py-3.5 rounded-xl font-bold tracking-tight transition-all active:scale-95 text-center"
                  >
                    View Product
                  </Link>
                  {!user ? (
                    <Link to="/register" className="btn-ghost-dark px-7 py-3.5 rounded-xl font-bold text-center">
                      Apply for Wholesale
                    </Link>
                  ) : (
                    <Link to="/products/disposable-vapes" className="btn-ghost-dark px-7 py-3.5 rounded-xl font-bold text-center">
                      Browse Catalog
                    </Link>
                  )}
                </div>
              </div>

              {/* Right: floating product card */}
              <div key={`card-${heroProducts[heroIdx]?.id}`} className="hidden lg:flex justify-center animate-fade-in-up">
                <div className="relative w-72 xl:w-80">
                  <div className="absolute -inset-4 bg-brand-amber/8 rounded-3xl blur-2xl" />
                  <div className="glass-white rounded-2xl p-5 shadow-glass">
                    <div className="aspect-square rounded-xl overflow-hidden bg-background mb-4">
                      <img
                        src={getPrimaryImage(heroProducts[heroIdx])}
                        alt={heroProducts[heroIdx]?.name}
                        className="w-full h-full object-contain p-4"
                      />
                    </div>
                    <p className="text-[10px] uppercase tracking-widest text-brand-amber font-bold mb-1">
                      {heroProducts[heroIdx]?.category}
                    </p>
                    <h4 className="text-sm font-bold text-foreground line-clamp-2 leading-snug">
                      {heroProducts[heroIdx]?.name}
                    </h4>
                    {user && heroProducts[heroIdx]?.price != null && (
                      <p className="text-brand-amber font-black text-base mt-2">
                        ${heroProducts[heroIdx].price.toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

            </div>
          ) : (
            /* Static fallback — Enterprise Gateway copy */
            <div className="max-w-2xl space-y-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-amber/30 bg-brand-amber/10 text-brand-amber text-xs font-bold tracking-widest uppercase">
                <LuShieldCheck size={12} />
                Premium B2B Supply Chain
              </div>
              <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tighter text-white leading-[1.05]">
                Modern Wholesale.<br />
                Built for{" "}
                <span className="text-brand-amber">Scale.</span>
              </h1>
              <p className="text-lg text-slate-400 max-w-lg leading-relaxed">
                Precision logistics meeting premium product curation. We empower DFW retailers with the inventory they need to dominate their markets.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                {!user ? (
                  <Link
                    to="/register"
                    className="bg-brand-amber hover:brightness-110 text-white px-8 py-4 rounded-xl font-bold tracking-tight transition-all active:scale-95 text-center"
                  >
                    Apply for Wholesale Access
                  </Link>
                ) : (
                  <Link
                    to="/account"
                    className="bg-brand-amber hover:brightness-110 text-white px-8 py-4 rounded-xl font-bold tracking-tight transition-all active:scale-95 text-center"
                  >
                    My Account
                  </Link>
                )}
                <Link
                  to="/products/disposable-vapes"
                  className="btn-ghost-dark px-8 py-4 rounded-xl font-bold text-center"
                >
                  View Collections
                </Link>
              </div>
            </div>
          )}

          {/* Carousel controls */}
          {heroProducts.length > 1 && (
            <div className="flex items-center gap-6 mt-10">
              <button
                onClick={goPrev}
                className="w-9 h-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/15 text-white flex items-center justify-center transition-all cursor-pointer"
                aria-label="Previous slide"
              >
                <LuChevronLeft size={16} />
              </button>
              <div className="flex items-center gap-2">
                {heroProducts.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      i === heroIdx ? "w-6 h-2 bg-brand-amber" : "w-2 h-2 bg-white/25 hover:bg-white/50"
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={goNext}
                className="w-9 h-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/15 text-white flex items-center justify-center transition-all cursor-pointer"
                aria-label="Next slide"
              >
                <LuChevronRight size={16} />
              </button>
              <span className="text-slate-500 text-xs font-semibold ml-1">
                {heroIdx + 1} / {heroProducts.length}
              </span>
            </div>
          )}
        </div>

        {/* ── Trust stats bar — Enterprise Gateway signature ── */}
        <div className="absolute bottom-0 left-0 right-0 z-20 border-t border-white/8" style={{ background: "rgba(15,23,42,0.82)", backdropFilter: "blur(16px)" }}>
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 grid grid-cols-2 md:grid-cols-4 gap-6">
            {TRUST_STATS.map(({ stat, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-1 h-8 rounded-full bg-brand-amber flex-shrink-0" />
                <div>
                  <p className="text-white font-extrabold text-lg leading-none" style={{ fontFamily: "Lexend, sans-serif" }}>
                    {stat}
                  </p>
                  <p className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider mt-0.5">
                    {label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Brand Pillars ─────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 -mt-0 relative z-20 pt-16 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              Icon: LuTruck,
              title: "Fast Local Delivery",
              body: "24–48 hour fulfillment across the DFW area. Keep your shelves stocked without the wait.",
            },
            {
              Icon: LuPackage,
              title: "Bulk Inventory",
              body: "Direct-from-manufacturer partnerships ensuring massive volume on all high-demand products.",
            },
            {
              Icon: LuHeadphones,
              title: "Dedicated Support",
              body: "Personal account managers available for strategic consultation and order optimization.",
            },
          ].map(({ Icon, title, body }, i) => (
            <ScrollReveal key={title} delay={i * 0.1}>
              <div className="bg-card p-8 rounded-2xl border border-border shadow-brand group hover:-translate-y-1.5 hover:shadow-blue hover:border-brand-amber/20 transition-all duration-300 h-full">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-amber/15 to-brand-amber/5 border border-brand-amber/15 flex items-center justify-center mb-6 text-brand-amber group-hover:from-brand-amber group-hover:to-brand-navy-mid group-hover:text-white group-hover:border-brand-amber transition-all duration-300">
                  <Icon size={22} />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
                <p className="text-[var(--color-muted-foreground)] text-sm leading-relaxed">{body}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* ── Browse by Category ────────────────────────────────────────────────── */}
      <section className="bg-brand-navy py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <ScrollReveal>
            <div className="flex items-end justify-between mb-16">
              <div>
                <span className="label-overline block mb-2">Departments</span>
                <h2 className="text-3xl font-bold tracking-tight text-white">Browse by Category</h2>
                <p className="text-slate-500 mt-1 text-sm">Strategically curated product verticals.</p>
              </div>
              <Link
                to="/products/disposable-vapes"
                className="hidden md:flex items-center gap-1 text-brand-amber font-semibold text-sm hover:gap-2 transition-all"
              >
                View all <LuArrowRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-3 md:grid-cols-5 gap-6 md:gap-10">
            {CATEGORIES.map(({ label, path }) => (
              <Link key={label} to={path} className="group flex flex-col items-center gap-4">
                <div className="relative w-full aspect-square overflow-hidden rounded-2xl border-2 border-white/10 bg-white/5 p-1 transition-all duration-300 group-hover:border-brand-amber/50 group-hover:bg-brand-amber/5">
                  <img
                    src={categoryImages[label]}
                    alt={label}
                    className="w-full h-full object-cover rounded-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"
                    loading="lazy"
                  />
                </div>
                <span className="text-sm font-bold tracking-tight text-slate-400 group-hover:text-brand-amber transition-colors text-center">
                  {label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── New Arrivals ──────────────────────────────────────────────────────── */}
      <FeaturedProducts title="New Arrivals" products={newProducts} />

      {/* ── Partner CTA ───────────────────────────────────────────────────────── */}
      <section className="py-24 bg-brand-dark-high dark:bg-[#0E1223] relative overflow-hidden">
        {/* Decorative — subtle blue circles */}
        <div className="absolute right-0 top-0 w-96 h-96 opacity-30 pointer-events-none">
          <svg className="w-full h-full text-brand-amber" fill="none" viewBox="0 0 200 200">
            <circle cx="200" cy="0" r="180" stroke="currentColor" strokeWidth="0.5" />
            <circle cx="200" cy="0" r="140" stroke="currentColor" strokeWidth="0.5" />
            <circle cx="200" cy="0" r="100" stroke="currentColor" strokeWidth="0.5" />
            <circle cx="200" cy="0" r="60"  stroke="currentColor" strokeWidth="0.5" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
          <ScrollReveal direction="right">
            <div>
              <span className="label-overline block mb-4">Partner Program</span>
              <h2 className="text-4xl font-bold tracking-tighter text-brand-navy dark:text-foreground mb-6 leading-tight">
                Partner with an<br />Established Leader.
              </h2>
              <p className="text-[var(--color-muted-foreground)] text-lg mb-8 max-w-lg leading-relaxed">
                Join successful retailers who rely on Khoja Distributions for reliable sourcing, aggressive wholesale pricing, and market-leading expertise.
              </p>
              <ul className="space-y-4">
                {[
                  "Tax-exempt purchasing for qualified businesses",
                  "Access to exclusive brand launches",
                  "Tiered bulk discount architecture",
                ].map(item => (
                  <li key={item} className="flex items-center gap-3 text-slate-700 dark:text-slate-300 font-medium">
                    <div className="w-5 h-5 rounded-full bg-brand-amber/15 border border-brand-amber/30 flex items-center justify-center flex-shrink-0">
                      <LuCircleCheck size={12} className="text-brand-amber" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.15}>
            <div className="bg-card p-10 rounded-3xl border border-border shadow-brand-lg">
              <div className="flex items-center gap-2 mb-6">
                <LuZap size={18} className="text-brand-amber" />
                <span className="text-brand-amber font-bold text-sm uppercase tracking-widest">Quick Start</span>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">Ready to get started?</h3>
              <p className="text-[var(--color-muted-foreground)] text-sm mb-8 leading-relaxed">
                Create your wholesale account to unlock bulk pricing, live inventory, and dedicated support.
              </p>
              <div className="space-y-3">
                {!user ? (
                  <>
                    <Link
                      to="/register"
                      className="block w-full bg-brand-amber hover:brightness-110 text-white font-bold py-4 rounded-xl text-center transition-all active:scale-95"
                    >
                      Apply for Wholesale Access
                    </Link>
                    <Link
                      to="/account"
                      className="block w-full btn-secondary font-bold py-4 rounded-xl text-center transition-all active:scale-95"
                    >
                      Sign In to Existing Account
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/products/disposable-vapes"
                      className="block w-full bg-brand-amber hover:brightness-110 text-white font-bold py-4 rounded-xl text-center transition-all active:scale-95"
                    >
                      Browse Catalog
                    </Link>
                    <Link
                      to="/account"
                      className="block w-full btn-secondary font-bold py-4 rounded-xl text-center transition-all active:scale-95"
                    >
                      My Account
                    </Link>
                  </>
                )}
              </div>
              <p className="text-[10px] text-center text-slate-400 uppercase tracking-widest mt-6 flex items-center justify-center gap-1.5">
                <LuShieldCheck size={11} className="text-slate-400" />
                Secure encrypted registration
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── Best Sellers ──────────────────────────────────────────────────────── */}
      <FeaturedProducts title="Best Sellers" products={bestSellers} dark />
    </div>
  );
}

export default Home;
